/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Tabbable Mode helpers
 */
import { head } from 'ramda';
import { last } from 'ramda';
import { pipe } from 'ramda';

// definition for Element types that can receive focus
export type FocusableElement = HTMLElement | SVGElement;

/**
 * Query selector to return all tabbable elements
 * @param allowAllTabIndex This param is optional. The default value is false. To include the element with tabIndex='-1', specify allowAllTabIndex to true.
 */
const findTabbableQuery = (allowAllTabIndex?: boolean) =>
  [
    'a:not([name]):not([disabled])',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    allowAllTabIndex ? '[tabIndex]' : "[tabIndex]:not([tabIndex='-1'])",
    "[contenteditable]:not([contenteditable='false'])"
  ].join(', ');

/**
 * Use tabbableQuery selector to determine if an element is tabbable
 * @param element the element
 * @param allowAllTabIndex This param is optional. The default value is false. To include the element with tabIndex='-1', specify allowAllTabIndex to true
 */
const isTabbableElement = (element: FocusableElement, allowAllTabIndex?: boolean) =>
  element.matches(findTabbableQuery(allowAllTabIndex));

/**
 * All tabbable child elements in a node.
 * @param el the node
 * @param includeItself This param is optional. The default value is false. To include the current node, specify includeItself to true
 * @param allowAllTabIndex This param is optional. The default value is false. To include the element with tabIndex='-1', specify allowAllTabIndex to true.
 */
const allTabbableElements = (
  el: FocusableElement,
  includeItself?: boolean,
  allowAllTabIndex?: boolean
) => {
  const tabbableQuery = findTabbableQuery(allowAllTabIndex);
  const tabbable = Array.from(el.querySelectorAll(tabbableQuery)).filter((element) => {
    // @ts-ignore
    // this is to ensure the input isn't hidden
    return element.offsetParent !== null && (allowAllTabIndex || element.tabIndex !== -1);
  }) as FocusableElement[];

  if (includeItself && isTabbableElement(el, allowAllTabIndex)) {
    tabbable.push(el);
  }
  return tabbable;
};

// All tabbable child elements in a node,  or the container itself if there is none
const tabbablesOrContainer = (element: FocusableElement) => {
  const tabbableElements = allTabbableElements(element);
  return tabbableElements.length ? tabbableElements : [element];
};

// First tabbable child element in a node, or the container itself if there is none
const firstTabbableOrContainer = pipe(tabbablesOrContainer, head);

/**
 * Focus Helpers
 */

// preventScroll safe focus setter
const focusOn = (element: FocusableElement) => {
  element.focus({ preventScroll: true });
};

// focus on first tabbable in a container or container itself if there is none
const focusWithin = (element: FocusableElement) => {
  const firstTabbable = firstTabbableOrContainer(element);
  focusOn(firstTabbable as FocusableElement);
};

// focus on initial tabstop
const focusOnStart = pipe(tabbablesOrContainer, head, focusOn);

// focus on last tabstop
const focusOnEnd = pipe(tabbablesOrContainer, last, focusOn);

// get active element from document parent of the node
const getActiveElement = (node?: Node) => {
  const conditionalDocument = node?.ownerDocument ?? document;
  return conditionalDocument.activeElement as FocusableElement;
};

// get active element from document parent of the node
const getBodyElement = (node?: Node) => {
  const conditionalDocument = node?.ownerDocument ?? document;
  return conditionalDocument.body;
};

export {
  focusOn, // preventScroll safe focus setter
  focusWithin, // focus on first tabbable in a container or container itself if there is none
  focusOnEnd, // focus on last tabstop in a container
  focusOnStart, // focus on first tabstop in a container
  getActiveElement, // get active element from document parent of the node
  getBodyElement, // get the body element from document parent of the node
  isTabbableElement, // uses tabbableQuery selector to determine if an element is tabbable
  allTabbableElements // uses tabbableQuery selector to find all tabbable elements inside an element
};
